import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetAuth, isApiConfigured } from "@/services/http";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdminLead {
  id:           number;
  formType:     "enquiry" | "listing" | "contact";
  name:         string;
  email:        string | null;
  phone:        string | null;
  subject:      string | null;
  message:      string;
  isRead:       boolean;
  createdAt:    string;
  sourcePage:   string | null;
  businessName: string | null;
  ownerEmail:   string | null;
}

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO_LEADS: AdminLead[] = [
  { id: 1, formType: "enquiry", name: "Arun Shetty", email: "arun@example.com", phone: "+91 98440 11234", subject: "Menu enquiry", message: "Do you have special packages for corporate lunch? We have a team of 25.", isRead: true, createdAt: "2026-09-12T10:32:00Z", sourcePage: "/business/mangalore-harbour-fish-house", businessName: "Mangalore Harbour Fish House", ownerEmail: "owner1@coastal.in" },
  { id: 2, formType: "enquiry", name: "Priya Kamath", email: "priya@example.com", phone: "+91 97410 55678", subject: "Booking enquiry", message: "Can I book a table for 4 on Saturday evening? Please confirm availability.", isRead: false, createdAt: "2026-09-11T16:10:00Z", sourcePage: "/business/mangalore-harbour-fish-house", businessName: "Mangalore Harbour Fish House", ownerEmail: "owner1@coastal.in" },
  { id: 3, formType: "listing", name: "Suresh Bhat", email: "sbhat@bizz.in", phone: "+91 98451 77890", subject: "New listing request", message: "I want to list my bakery in Udupi. Business plan sounds right for us.", isRead: false, createdAt: "2026-09-10T08:05:00Z", sourcePage: "/list-your-business", businessName: null, ownerEmail: null },
  { id: 4, formType: "enquiry", name: "Lakshmi Hegde", email: "lakshmi@net.in", phone: null, subject: "Catering query", message: "Do you do catering for weddings? We have an event in September near Mangalore.", isRead: true, createdAt: "2026-09-09T14:30:00Z", sourcePage: "/business/udupi-sri-krishna-bhavan", businessName: "Udupi Sri Krishna Bhavan", ownerEmail: "owner2@coastal.in" },
  { id: 5, formType: "contact", name: "Rajan Mallya", email: "rajan@mail.com", phone: "+91 96321 88765", subject: "Partnership query", message: "Would your platform be open to featuring local food bloggers? Let's connect.", isRead: true, createdAt: "2026-09-08T11:55:00Z", sourcePage: "/contact", businessName: null, ownerEmail: null },
  { id: 6, formType: "listing", name: "Dr. Nisha D'Silva", email: "nisha@dental.in", phone: "+91 94489 12678", subject: "Premium listing", message: "Interested in the Premium plan for my dental clinic in Mangaluru. Please reach out.", isRead: false, createdAt: "2026-09-07T09:20:00Z", sourcePage: "/list-your-business", businessName: null, ownerEmail: null },
  { id: 7, formType: "enquiry", name: "Ganesh Poojary", email: "ganesh@poojary.in", phone: "+91 90088 55678", subject: null, message: "What are your lunch timings and do you take large group reservations?", isRead: true, createdAt: "2026-09-06T12:40:00Z", sourcePage: "/business/puttur-chicken-corner", businessName: "Puttur Chicken Corner", ownerEmail: "owner3@coastal.in" },
  { id: 8, formType: "contact", name: "Asha Fernandes", email: "asha@news.in", phone: null, subject: "Media collaboration", message: "I am a journalist covering coastal Karnataka for a travel magazine. Interested in featuring Coastal Bizz Connect.", isRead: false, createdAt: "2026-09-05T15:00:00Z", sourcePage: "/contact", businessName: null, ownerEmail: null },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  enquiry: "Enquiry",
  listing: "Listing Request",
  contact: "Contact",
};

const TYPE_COLORS: Record<string, string> = {
  enquiry: "bg-primary-100 text-primary-700",
  listing: "bg-accent-100 text-accent-700",
  contact: "bg-secondary-100 text-secondary-700",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LeadsModeration() {
  const { accessToken } = useAuth();
  const [leads,    setLeads]    = useState<AdminLead[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState<"all" | AdminLead["formType"] | "unread">("all");
  const [expandId, setExpandId] = useState<number | null>(null);
  const [search,   setSearch]   = useState("");

  useEffect(() => {
    if (!isApiConfigured() || !accessToken) {
      setTimeout(() => { setLeads(DEMO_LEADS); setLoading(false); }, 350);
      return;
    }
    apiGetAuth<{ data: AdminLead[] }>("/admin/leads", accessToken)
      .then((res) => setLeads(res.data ?? []))
      .catch(() => setLeads(DEMO_LEADS))
      .finally(() => setLoading(false));
  }, [accessToken]);

  const unread = leads.filter((l) => !l.isRead).length;

  const filtered = leads.filter((l) => {
    const matchTab =
      tab === "all" ? true :
      tab === "unread" ? !l.isRead :
      l.formType === tab;

    const q = search.trim().toLowerCase();
    const matchSearch = !q || [l.name, l.email ?? "", l.subject ?? "", l.message, l.businessName ?? ""]
      .join(" ").toLowerCase().includes(q);

    return matchTab && matchSearch;
  });

  const tabs: { key: typeof tab; label: string }[] = [
    { key: "all",     label: `All (${leads.length})` },
    { key: "unread",  label: `Unread (${unread})` },
    { key: "enquiry", label: "Enquiries" },
    { key: "listing", label: "Listing Requests" },
    { key: "contact", label: "Contact" },
  ];

  const stats = [
    { label: "Total Leads",      value: leads.length, icon: "ri-mail-open-line",     color: "text-primary-600",   bg: "bg-primary-50"   },
    { label: "Unread",           value: unread,        icon: "ri-mail-unread-line",    color: "text-rose-600",      bg: "bg-rose-50"      },
    { label: "Listing Requests", value: leads.filter((l) => l.formType === "listing").length, icon: "ri-store-2-line", color: "text-accent-600", bg: "bg-accent-50" },
    { label: "Contact Msgs",     value: leads.filter((l) => l.formType === "contact").length, icon: "ri-message-3-line", color: "text-secondary-600", bg: "bg-secondary-50" },
  ];

  return (
    <div>
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.bg}`}>
              <i className={`${s.icon} text-xl ${s.color}`} />
            </div>
            <div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="text-xs text-foreground-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-background-200 bg-background-50">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-6 py-4">
          <h2 className="font-heading text-base font-bold text-foreground-950">All Platform Leads</h2>
          {/* Search */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              type="text"
              placeholder="Search leads…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-56 rounded-lg border border-background-300 bg-background-100 pl-8 pr-3 text-xs text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-100"
            />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-background-200 px-5 pt-3">
          {tabs.map((t) => (
            <button
              key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`cursor-pointer whitespace-nowrap border-b-2 px-3 pb-2.5 text-xs font-semibold transition-colors ${
                tab === t.key
                  ? "border-primary-500 text-primary-700"
                  : "border-transparent text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No leads found</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {filtered.map((lead) => (
              <div key={lead.id} className={!lead.isRead ? "bg-primary-50/30" : ""}>
                <button
                  type="button"
                  onClick={() => setExpandId(expandId === lead.id ? null : lead.id)}
                  className="flex w-full cursor-pointer items-start gap-4 px-6 py-4 text-left hover:bg-background-50"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {lead.name[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`text-sm font-semibold ${!lead.isRead ? "text-foreground-950" : "text-foreground-700"}`}>
                        {lead.name}
                      </p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TYPE_COLORS[lead.formType]}`}>
                        {TYPE_LABELS[lead.formType]}
                      </span>
                      {!lead.isRead && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      {lead.businessName ? `→ ${lead.businessName} · ` : ""}{lead.subject ?? lead.message.slice(0, 55)}
                      {!lead.subject && lead.message.length > 55 ? "…" : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <p className="text-[11px] text-foreground-400">{timeAgo(lead.createdAt)}</p>
                    <i className={`text-sm text-foreground-400 ${expandId === lead.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                  </div>
                </button>

                {expandId === lead.id && (
                  <div className="mx-6 mb-4 rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                      <div className="space-y-2">
                        <p className="text-foreground-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">From</span>
                          <br />{lead.name}
                        </p>
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} className="block text-primary-600 hover:underline">{lead.email}</a>
                        )}
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} className="block text-primary-600 hover:underline">{lead.phone}</a>
                        )}
                      </div>
                      <div className="space-y-1 text-xs text-foreground-500">
                        <p>Received: {new Date(lead.createdAt).toLocaleString("en-IN")}</p>
                        {lead.sourcePage && <p>Page: {lead.sourcePage}</p>}
                        {lead.businessName && (
                          <p>Business: <span className="font-medium text-foreground-700">{lead.businessName}</span></p>
                        )}
                        {lead.ownerEmail && (
                          <p>Owner: <a href={`mailto:${lead.ownerEmail}`} className="text-primary-600 hover:underline">{lead.ownerEmail}</a></p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-700">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground-500">Message</p>
                      {lead.message}
                    </div>
                    <div className="mt-4 flex gap-2">
                      {lead.email && (
                        <a href={`mailto:${lead.email}?subject=Re: ${lead.subject ?? "Your enquiry"}`}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600">
                          <i className="ri-reply-line text-sm" /> Reply
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100">
                          <i className="ri-phone-line text-sm" /> Call
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}